import axios from "axios";
import { useOidcAccessToken } from "@axa-fr/react-oidc";
import { routeErrorHandler } from "Helper/routerErrorHandler";
import { toast } from "react-toastify";
import { getAppConfig } from "../services/configService";
import { getStore } from "../storeRef";
import { showErrorDialog } from "../slices/errorDialogSlice";
import { showAccessDenied } from "../slices/accessDeniedSlice";
import { setAuthError } from "../slices/authSlice";

export let API = null;
export let AccountsAPI = null;
export let ReportsAPI = null;
export let DocumentAPI = null;
export let CompanyAPI = null;

const STORAGE_KEYS = {
  CASE_API: "caseApiUrl",
  ACCOUNTS_API: "accountsApiUrl",
  REPORTS_API: "reportsApiUrl",
  DOCUMENT_API: "documentApiUrl",
  COMPANY_API: "companyApiUrl",
};

export let TOKEN = ``;
export let baseURL = localStorage.getItem(STORAGE_KEYS.CASE_API);
export let CaseAPI = localStorage.getItem(STORAGE_KEYS.CASE_API);
export let ReportURL = localStorage.getItem(STORAGE_KEYS.REPORTS_API);
export let AccountsURL = localStorage.getItem(STORAGE_KEYS.ACCOUNTS_API);

export const TokenHandler = () => {
  const { accessToken } = useOidcAccessToken();
  TOKEN = "Bearer " + accessToken;
  return;
};

const authUrl = (path = "") => {
  const { authServerUrl } = getAppConfig();
  return new URL(path, authServerUrl.replace(/\/+$/, "") + "/").toString();
};

export const authGet = (path, config = {}) => {
    const url = authUrl(path);

    return axios.get(url, {
        ...config,
        headers: {
            ...(config.headers || {}),
            Authorization: TOKEN,
        },
    });
};


const unwrapBlobErrorData = async (error) => {
  const data = error.response?.data;
  if (!(data instanceof Blob)) {
    return;
  }

  try {
    const text = await data.text();
    error.response.data = JSON.parse(text);
  } catch {
  }
};

const handleApiError = (error) => {
  if (error.config?.suppressErrorToast) {
    return;
  }

  const store = getStore();
  const status = error.response?.status;
  const data = error.response?.data;

  const getMessage = () => {
    return data?.message ||
      data?.userMessage ||
      data?.errorMessage ||
      data?.error ||
      'An error occurred';
  };

  switch (status) {
    case 404:
      toast.warn(getMessage());
      break;

    case 400:
      toast.error(getMessage());
      break;

    case 460:
      toast.error(getMessage());
      break;

    case 463:
      store?.dispatch(showErrorDialog(getMessage()));
      break;

    case 464:
      break;

    case 403:
      store?.dispatch(showAccessDenied(getMessage()));
      break;

    case 401:
      store?.dispatch(setAuthError());
      break;

    case 500:
    default:
      if (status >= 500) {
        toast.error('An unexpected error occurred');
      }
      break;
  }
};

const createAxiosInstance = (baseURL) => {
  const instance = axios.create({ baseURL });

  instance.interceptors.request.use((req) => {
    if (TOKEN) {
      req.headers.Authorization = TOKEN;
    }
    return req;
  });

  instance.interceptors.response.use(
    (response) => response,
    async (error) => {
      await unwrapBlobErrorData(error);
      handleApiError(error);
      const requestURL = error.config?.url;
      const customError = routeErrorHandler(requestURL, error);
      return Promise.reject(customError);
    }
  );

  return instance;
};

export const fetchAndSetBaseURLs = async () => {
  const userInfoUrl = authUrl("connect/userinfo");

  const response = await axios.get(userInfoUrl, {
    headers: { Authorization: TOKEN },
  });

  localStorage.setItem(STORAGE_KEYS.CASE_API, response.data.CaseAPIUrl);
  localStorage.setItem(STORAGE_KEYS.ACCOUNTS_API, response.data.AccountsAPIUrl);
  localStorage.setItem(STORAGE_KEYS.REPORTS_API, response.data.ReportsAPIUrl);
  localStorage.setItem(STORAGE_KEYS.DOCUMENT_API, response.data.DocumentAPIUrl);
  localStorage.setItem(STORAGE_KEYS.COMPANY_API, response.data.CompanyAPIUrl);

  baseURL = response.data.CaseAPIUrl;
  CaseAPI = response.data.CaseAPIUrl;
  ReportURL = response.data.ReportsAPIUrl;
  AccountsURL = response.data.AccountsAPIUrl;

  if (!API) {
    API = createAxiosInstance(localStorage.getItem(STORAGE_KEYS.CASE_API));
    AccountsAPI = createAxiosInstance(
      localStorage.getItem(STORAGE_KEYS.ACCOUNTS_API)
    );
    ReportsAPI = createAxiosInstance(localStorage.getItem(STORAGE_KEYS.REPORTS_API));
    DocumentAPI = createAxiosInstance(
      localStorage.getItem(STORAGE_KEYS.DOCUMENT_API)
    );
    CompanyAPI = createAxiosInstance(localStorage.getItem(STORAGE_KEYS.COMPANY_API));
  }

  return { API, AccountsAPI, ReportsAPI, DocumentAPI, CompanyAPI };
};

export const getAPIInstance = async () => {
  if (!API) {
    await fetchAndSetBaseURLs();
  }
  return API;
};
